import { Button, Drawer, Flex, Space, Typography } from 'antd';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { faChevronDown, faChevronUp, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isNumber } from 'lodash';
import useIsMobile from '../../helpers/customHooks/useIsMobile';
import { subscribeToCollapseDrawer } from '../../helpers/drawerControl';
import './index.less';

const { Text } = Typography;

// `peekRows` (with `rowSelector` naming what a row is) rests the half-open drawer on the title and
// that many list rows rather than on half the screen — a fraction leaves a row cut off, which is
// what tells people the list scrolls. `openOnLoad={false}` keeps the drawer at its handle once the
// content has loaded, leaving the map as much room as possible.
const ContentDrawer = forwardRef(({ children, goBack, loading, nudge, handleLabel, nudgeDistance, title, peekRows, rowSelector, openOnLoad = true, drawerProps = {} }, ref) => {
	const closeStateHeight = 56;
	// Gap between the top of the drawer and the map controls resting on it.
	const controlGap = 16;
	const isMobile = useIsMobile();
	const [currentHeight, setCurrentHeight] = useState(closeStateHeight);
	const [headerHeight, setHeaderHeight] = useState(0);
	const [yStart, setYStart] = useState(undefined);
	const [yMoved, setYMoved] = useState(undefined);
	const [drawerContainerHeight, setDrawerContainerHeight] = useState(0);
	const [peekHeight, setPeekHeight] = useState(0);
	const [drawerState, setDrawerState] = useState('close'); // half_open, open
	const timeout = useRef(null);

	// Never taller than the half-screen it replaces. Until the rows have rendered there is nothing
	// to measure, and the drawer holds at its handle rather than rising to half and then dropping.
	const halfOpenHeight = peekHeight ? Math.min(peekHeight, drawerContainerHeight / 2) : drawerContainerHeight / 2;
	const awaitingPeek = Boolean(peekRows) && !peekHeight;
	// The height follows the state, never the other way round: syncing the two both ways let a render
	// that changed both leave them out of step, and each then kept resetting the other.
	const height = drawerState === 'open' ? drawerContainerHeight : drawerState === 'half_open' ? halfOpenHeight : closeStateHeight;
	// Set when something outside the drawer asked it to get out of the way (the map
	// centring on the user's position). Holds off the automatic half-open below so the
	// collapse isn't undone a moment later by content that finished loading; cleared as
	// soon as new content loads or the user moves the drawer themselves.
	const suppressAutoOpenRef = useRef(false);

	// FIXME
	const handleTouchStart = (e) => {
		e.preventDefault();
		suppressAutoOpenRef.current = false;
		setYStart(e.touches[0].pageY);
	};

	const handleTouchMove = (e) => {
		timeout.current = setTimeout(() => {
			setYMoved(e.touches[0].pageY);
		}, 50);
		// let new_height = currentHeight + distance;

		// if (new_height < (drawerContainerHeight / 4)) {
		// 	new_height = closeStateHeight;
		// } else if (new_height > 1.25 * (drawerContainerHeight / 2)) {
		// 	new_height = drawerContainerHeight;
		// } else {
		// 	new_height = drawerContainerHeight / 2;
		// }

		// setHeight(new_height);
	};

	const handleTouchEnd = (e) => {
		setCurrentHeight(height);
		clearTimeout(timeout.current);
	};

	const toggleDrawer = (rise = true) => {
		suppressAutoOpenRef.current = false;
		if (drawerState === 'half_open') {
			setDrawerState(rise ? 'open' : 'close');
		}
		if (drawerState === 'open') {
			setDrawerState(rise ? 'open' : 'half_open');
		}
		if (drawerState === 'close') {
			setDrawerState('half_open');
		}
	};

	useEffect(() => {
		if (!isMobile) return;
		if (!yMoved) return;
		const direction = yStart - yMoved;
		if (direction < -200) {
			setDrawerState('close');
		}
		if (direction > 0 && direction < 200 || direction < 0 && direction > -200) {
			setDrawerState('half_open');
		}
		if (direction > 200) {
			setDrawerState('open');
		}
	}, [yMoved]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		const drawer_body = document.querySelector('.ant-drawer.ant-drawer-open .ant-drawer-body');
		const drawer_container = document.querySelector('.ant-drawer.ant-drawer-open');
		const drawer = document.querySelector('.custom-header');

		if (drawer_body)
			drawer_body.id = 'scrollable-content';

		if (drawer_container) {
			const bound = drawer_container.getBoundingClientRect();
			setDrawerContainerHeight(bound.height);
		}

		// Rows lay out at the same place inside the drawer whatever its height, so where the peek
		// should end can be read off them at any time. The last measurement is kept while a list
		// is swapped for another, so the drawer doesn't drop to its handle in between.
		const drawer_wrapper = drawer_container?.querySelector('.ant-drawer-content-wrapper');
		const rows = peekRows && drawer_wrapper ? drawer_wrapper.querySelectorAll(rowSelector) : [];
		if (rows.length) {
			const scrollTop = drawer_body ? drawer_body.scrollTop : 0;
			const offsetOf = (row) => row.getBoundingClientRect().top - drawer_wrapper.getBoundingClientRect().top + scrollTop;
			const wholeRows = Math.floor(peekRows);
			const cutRow = rows[wholeRows];
			const lastRow = rows[Math.min(wholeRows, rows.length) - 1];

			// Fewer rows than asked for: show them all, with the body's bottom padding below.
			const peek = cutRow
				? offsetOf(cutRow) + (peekRows - wholeRows) * cutRow.offsetHeight
				: offsetOf(lastRow) + lastRow.offsetHeight + parseFloat(getComputedStyle(drawer_body).paddingBottom || 0);
			if (Math.abs(peek - peekHeight) >= 1) setPeekHeight(Math.round(peek));
		}

		if (drawer) {
			drawer.addEventListener('touchstart', handleTouchStart, { passive: true });
			drawer.addEventListener('touchmove', handleTouchMove, { passive: true });
			drawer.addEventListener('touchend', handleTouchEnd);
		}

		return () => {
			if (drawer) {
				drawer.removeEventListener('touchstart', handleTouchStart);
				drawer.removeEventListener('touchmove', handleTouchMove);
				drawer.removeEventListener('touchend', handleTouchEnd);
			}
		};

	});

	useEffect(() => {
		if (!isMobile) return;
		if (nudge) {
			setHeaderHeight(nudgeDistance / 100 * drawerContainerHeight);
		}
		else {
			const drawer = document.querySelector('.custom-header');
			if (drawer)
				setHeaderHeight(drawer.clientHeight);
		}
	}, [nudge, nudgeDistance, drawerContainerHeight, isMobile]);

	// Lower the drawer on request from outside (see helpers/drawerControl).
	useEffect(() => {
		if (!isMobile) return undefined;

		return subscribeToCollapseDrawer(() => {
			suppressAutoOpenRef.current = true;
			setDrawerState('close');
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMobile]);

	useEffect(() => {
		if (!isMobile) return;

		if (loading) {
			// New content on the way: whatever the map asked for is stale now.
			suppressAutoOpenRef.current = false;
			setDrawerState('close');
		}
		else
			if (headerHeight && !suppressAutoOpenRef.current && !awaitingPeek) {
				setDrawerState(openOnLoad ? 'half_open' : 'close');
			}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [headerHeight, loading, isMobile, awaitingPeek, openOnLoad]);

	useEffect(() => {
		// Fully open, the drawer covers the map controls; they stay where they were.
		if (!isMobile || drawerState === 'open') return;

		// The floor list above the floor selector is sized in CSS from this.
		document.documentElement.style.setProperty('--control-bottom-position', `${height + controlGap}px`);

	}, [height, drawerState, isMobile]);

	return (
		isMobile ?
			<Drawer
				ref={ref}
				className="content-drawer"
				open
				height={height ? height : closeStateHeight}
				classNames={{ header: 'custom-header' }}
				placement="bottom"
				mask={false}
				onClose={undefined}
				destroyOnClose={true}
				closeIcon={goBack ? (
					<Button
						type="text"
						style={{ margin: 0, color: 'unset' }}
						onClick={goBack}
						icon={
							<FontAwesomeIcon
								icon={faCircleXmark}
								fontSize={22}
							/>
						}
					/>
				) : null}
				title={isMobile ? <>
					<div className="handlebar">
						<Flex
							gap={drawerState === 'half_open' ? 20 : 9}
							align="center"
						>
							{
								(drawerState === 'half_open' || drawerState === 'close') &&
								<Button
									type="link"
									icon={(
										<FontAwesomeIcon
											icon={faChevronUp}
											fontSize={22}
										/>
									)}
									onClick={() => toggleDrawer(true)}
								/>
							}
							{
								(drawerState === 'half_open' || drawerState === 'open') &&
								<Button
									type="link"
									icon={(
										<FontAwesomeIcon
											icon={faChevronDown}
											fontSize={22}
										/>
									)}
									onClick={() => toggleDrawer(false)}
								/>
							}

							{
								drawerState === 'close' &&
								<Text style={{ fontWeight: handleLabel ? 500 : 400, fontSize: 14, opacity: handleLabel ? 1 : .5 }}>{handleLabel && drawerState === 'close' ? handleLabel : drawerState === 'close' ? 'Tap to see details' : null}</Text>
							}
						</Flex>
					</div>
					{title()}
				</> : undefined}
				{...drawerProps}
			>
				{children}
			</Drawer> :
			children
	);
});

export default ContentDrawer;